import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Select, Button, message, Spin } from 'antd';
import { BookOutlined, FileTextOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { textbookVersionAPI, textbookAPI } from '../../services/api';
import './Login.css';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [loadingTextbooks, setLoadingTextbooks] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // 防止重复初始化
  const [isRedirecting, setIsRedirecting] = useState(false); // 防止跳转过程中重复执行
  const redirectTimeoutRef = useRef(null); // 存储setTimeout引用，用于清理
  const [form] = Form.useForm();
  const navigate = useNavigate();
  
  // 教材数据
  const [textbookVersions, setTextbookVersions] = useState([]);
  const [textbooks, setTextbooks] = useState([]);
  const [units, setUnits] = useState([]);
  
  // 选择的数据
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedTextbook, setSelectedTextbook] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  
  // 初始化检查localStorage并根据结果决定是否加载数据
  useEffect(() => {
    // 防止重复初始化
    if (isInitialized) return;
    
    const shouldLoadData = checkLocalStorageAndRedirect();
    if (shouldLoadData) {
      loadTextbookVersions();
    }
    
    setIsInitialized(true);
  }, []); // 移除isInitialized依赖，只在组件首次挂载时执行

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  /**
   * 检查localStorage中是否已存在教材选择数据，如果存在则直接跳转
   * @returns {boolean} 是否需要加载教材版本数据
   */
  const checkLocalStorageAndRedirect = () => {
    try {
      // 如果已经在跳转过程中，直接返回
      if (isRedirecting) {
        return false;
      }
      
      // 检查token是否存在
      const token = localStorage.getItem('token');
      // 检查教材选择数据是否存在
      const selectedTextbookData = localStorage.getItem('selectedTextbook');
      
      if (token && selectedTextbookData) {
        const parsedData = JSON.parse(selectedTextbookData);
        
        // 验证数据完整性：需要有version、textbook、unit信息
        if (parsedData?.version?.id && 
            parsedData?.textbook?.id && 
            parsedData?.unit?.id) {
          
          // 设置跳转状态，防止重复执行
          setIsRedirecting(true);
          
          // 显示欢迎消息
          message.success(`欢迎回来！已自动选择: ${parsedData.version.title} - ${parsedData.textbook.title} - ${parsedData.unit.title}`);
          
          // 延迟跳转，确保消息显示
          redirectTimeoutRef.current = setTimeout(() => {
            navigate('/word');
          }, 100);
          
          return false; // 不需要加载数据
        }
      }
      
      return true; // 需要加载数据
    } catch (error) {
      console.error('检查localStorage失败:', error);
      // 如果检查出错，清除可能损坏的数据
      localStorage.removeItem('selectedTextbook');
      localStorage.removeItem('token');
      return true; // 需要加载数据
    }
  };

  /**
   * 加载教材版本列表
   */
  const loadTextbookVersions = async () => {
    setLoadingVersions(true);
    try {
      const response = await textbookVersionAPI.getAll();
      const versions = response.data.map(version => ({
        value: version.id,
        label: version.title,
        data: version
      }));
      setTextbookVersions(versions);
    } catch (error) {
      console.error('加载教材版本失败:', error);
      message.error('加载教材版本失败，请检查网络连接');
    } finally {
      setLoadingVersions(false);
    }
  };

  /**
   * 加载指定版本下的教材列表
   * @param {number} versionId - 版本ID
   */
  const loadTextbooks = async (versionId) => {
    setLoadingTextbooks(true);
    try {
      const response = await textbookVersionAPI.getTextbooks(versionId);
      const textbookList = response.data.map(textbook => ({
        value: textbook.id,
        label: textbook.title,
        data: textbook
      }));
      setTextbooks(textbookList);
    } catch (error) {
      console.error('加载教材列表失败:', error);
      message.error('加载教材列表失败，请重试');
      setTextbooks([]);
    } finally {
      setLoadingTextbooks(false);
    }
  };

  /**
   * 加载指定教材下的单元列表
   * @param {number} textbookId - 教材ID
   */
  const loadUnits = async (textbookId) => {
    setLoadingUnits(true);
    try {
      const response = await textbookAPI.getUnits(textbookId);
      const unitList = response.data.map(unit => ({
        value: unit.id,
        label: unit.title,
        data: unit
      }));
      setUnits(unitList);
    } catch (error) {
      console.error('加载单元列表失败:', error);
      message.error('加载单元列表失败，请重试');
      setUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  };

  /**
   * 处理教材版本选择
   * @param {number} versionId - 版本ID
   * @param {Object} option - 选择的版本选项
   */
  const handleVersionChange = (versionId, option) => {
    setSelectedVersion(option.data);
    setSelectedTextbook(null);
    setSelectedUnit(null);
    
    // 清空后续选择
    setTextbooks([]);
    setUnits([]);
    form.setFieldsValue({
      textbook: undefined,
      unit: undefined
    });
    
    // 加载该版本下的教材列表
    loadTextbooks(versionId);
  };

  /**
   * 处理教材选择
   * @param {number} textbookId - 教材ID
   * @param {Object} option - 选择的教材选项
   */
  const handleTextbookChange = (textbookId, option) => {
    setSelectedTextbook(option.data);
    setSelectedUnit(null);
    
    // 清空单元选择
    setUnits([]);
    form.setFieldsValue({
      unit: undefined
    });
    
    // 加载该教材下的单元列表
    loadUnits(textbookId);
  };

  /**
   * 处理单元选择
   * @param {number} unitId - 单元ID
   * @param {Object} option - 选择的单元选项
   */
  const handleUnitChange = (unitId, option) => {
    setSelectedUnit(option.data);
  };

  /**
   * 提交表单，保存选择结果
   */
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // 构建要保存的数据
      const selectedData = {
        version: {
          id: selectedVersion.id,
          title: selectedVersion.title,
          description: selectedVersion.description
        },
        textbook: {
          id: selectedTextbook.id,
          title: selectedTextbook.title,
          grade: selectedTextbook.grade,
          term: selectedTextbook.term,
          publish_id: selectedTextbook.publish_id
        },
        unit: {
          id: selectedUnit.id,
          title: selectedUnit.title,
          textbook_id: selectedUnit.textbook_id
        },
        selectedAt: new Date().toISOString()
      };
      
      // 保存到localStorage
      localStorage.setItem('selectedTextbook', JSON.stringify(selectedData));
      
      // 触发自定义事件，通知其他组件 selectedTextbook 已更新
      const customEvent = new CustomEvent('customStorageChange', {
        detail: { key: 'selectedTextbook', value: selectedData }
      });
      window.dispatchEvent(customEvent);
      
      // 模拟登录token
      const mockToken = 'jwt-token-' + Date.now();
      localStorage.setItem('token', mockToken);
      
      message.success(`已选择: ${selectedVersion.title} - ${selectedTextbook.title} - ${selectedUnit.title}`);
      
      // 跳转到主页
      navigate('/');
    } catch (error) {
      console.error('保存选择失败:', error);
      message.error('保存选择失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-content">
          <Card className="login-card">
            <div className="login-header">
              <h1>大鱼同步课堂</h1>
              <p>请选择教材版本、教材和单元</p>
            </div>
            
            <Form
              form={form}
              name="textbook-selection"
              size="large"
              onFinish={handleSubmit}
              layout="vertical"
            >
              <Form.Item
                name="version"
                label="教材版本"
                rules={[
                  { required: true, message: '请选择教材版本' }
                ]}
              >
                <Select
                  placeholder="选择教材版本"
                  onChange={handleVersionChange}
                  suffixIcon={<BookOutlined />}
                  loading={loadingVersions}
                  options={textbookVersions}
                  notFoundContent={loadingVersions ? <Spin size="small" /> : '暂无数据'}
                />
              </Form.Item>
              
              <Form.Item
                name="textbook"
                label="教材"
                rules={[
                  { required: true, message: '请选择教材' }
                ]}
              >
                <Select
                  placeholder={textbooks.length === 0 ? "请先选择教材版本" : "选择教材"}
                  onChange={handleTextbookChange}
                  suffixIcon={<FileTextOutlined />}
                  disabled={textbooks.length === 0 && !loadingTextbooks}
                  loading={loadingTextbooks}
                  options={textbooks}
                  notFoundContent={
                    loadingTextbooks ? <Spin size="small" /> : 
                    textbooks.length === 0 ? '请先选择教材版本' : '暂无数据'
                  }
                />
              </Form.Item>
              
              <Form.Item
                name="unit"
                label="单元"
                rules={[
                  { required: true, message: '请选择单元' }
                ]}
              >
                <Select
                  placeholder={units.length === 0 ? "请先选择教材" : "选择单元"}
                  onChange={handleUnitChange}
                  suffixIcon={<UnorderedListOutlined />}
                  disabled={units.length === 0 && !loadingUnits}
                  loading={loadingUnits}
                  options={units}
                  notFoundContent={
                    loadingUnits ? <Spin size="small" /> : 
                    units.length === 0 ? '请先选择教材' : '暂无数据'
                  }
                />
              </Form.Item>
              
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="login-form-button"
                  loading={loading}
                  block
                >
                  开始学习
                </Button>
              </Form.Item>
            </Form>
            
            <div className="login-footer">
              <p>选择教材后即可开始学习</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;